import type { ConnectionStringIssue } from '#layers/data-model/utils/data-connectors/connection-string';
import * as z from 'zod/v4-mini';
import { nonEmptyString } from '#layers/base/public';
import { FormSchemaKind } from '#layers/block-engine/public';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import { PRIMITIVE_TYPES_UNSIGNED_INTEGER, SUPPORTED_PRIMITIVE_TYPES } from '#layers/data-model/constants/schema/primitive-type';
import { ConnectorType, defineConnector } from '#layers/data-model/types/data-connectors/define';
import { createConnectionStringOptionsChangeHandler, parseConnectionString } from '#layers/data-model/utils/data-connectors/connection-string';
import { caCertField, SslMode, sslModeOptions } from '#layers/data-model/utils/data-connectors/ssl';

// This is the canonical Engine representation, not public SQL Server URL syntax.
// verify-ca is omitted because tiberius always verifies the hostname with the chain.
const MSSQL_MODES = [SslMode.allow, SslMode.require, SslMode.verifyFull];
const MSSQL_SSL_MODE_OPTIONS = sslModeOptions(MSSQL_MODES).map((option) => option.value === SslMode.require
	? { ...option, label: '$t:connectors.mssql.ssl_mode_require' }
	: option);

const INVALID_CONNECTION_STRING = '$t:connectors.common.invalid_connection_string';
const STRICT_MODE_UNSUPPORTED = '$t:connectors.mssql.strict_mode_unsupported';
const INVALID_BOOLEAN = Symbol('invalid-boolean');

const mssqlSslSchema = z.strictObject({
	mode: z.enum(MSSQL_MODES),
	additional_ca_cert: z.optional(z.string()),
});

export const mssqlSchema = z.object({
	host: nonEmptyString(),
	port: z.number(),
	user: nonEmptyString(),
	password: nonEmptyString(),
	dbname: z.optional(z.string()),
	ssl: mssqlSslSchema,
});

/** The validated shape plus the form-only `connectionString` used to pre-fill fields. */
export type MssqlOptions = z.infer<typeof mssqlSchema> & {
	connectionString?: string;
};

export interface MssqlConnectionStringParseResult {
	options: Partial<MssqlOptions>;
	issues: ConnectionStringIssue[];
	error?: string;
}

/** Read a query parameter case-insensitively; SQL Server connection keywords are case-insensitive. */
function getParamCaseInsensitive(params: URLSearchParams, name: string): string | undefined {
	const target = name.toLowerCase();

	for (const [key, value] of params) {
		if (key.toLowerCase() === target) return value;
	}

	return undefined;
}

function hasParamCaseInsensitive(params: URLSearchParams, names: readonly string[]): boolean {
	const normalizedNames = new Set(names.map((name) => name.toLowerCase()));
	return [...params.keys()].some((key) => normalizedNames.has(key.toLowerCase()));
}

function hasParamValueCaseInsensitive(params: URLSearchParams, name: string, value: string): boolean {
	const normalizedName = name.toLowerCase();
	const normalizedValue = value.toLowerCase();

	return [...params].some(([key, candidate]) =>
		key.toLowerCase() === normalizedName && candidate.toLowerCase() === normalizedValue,
	);
}

function parseBooleanKeyword(value: string | undefined): boolean | undefined | typeof INVALID_BOOLEAN {
	if (value === undefined) return undefined;

	const normalized = value.toLowerCase();
	if (['true', 'yes', '1'].includes(normalized)) return true;
	if (['false', 'no', '0'].includes(normalized)) return false;
	return INVALID_BOOLEAN;
}

function resolveMssqlSslMode(params: URLSearchParams): { mode?: SslMode; error?: string } {
	if (hasParamValueCaseInsensitive(params, 'encrypt', 'strict')) {
		return { error: STRICT_MODE_UNSUPPORTED };
	}

	if (hasParamCaseInsensitive(params, ['sslmode', 'ssl-mode'])) {
		return { error: INVALID_CONNECTION_STRING };
	}

	const trust = parseBooleanKeyword(getParamCaseInsensitive(params, 'trustServerCertificate'));
	if (trust === INVALID_BOOLEAN) return { error: INVALID_CONNECTION_STRING };

	const encryptValue = getParamCaseInsensitive(params, 'encrypt');

	if (encryptValue === undefined) {
		return { mode: trust === true ? SslMode.require : SslMode.verifyFull };
	}

	const encrypt = encryptValue.toLowerCase();

	if (['false', 'no', 'optional', '0'].includes(encrypt)) {
		return trust === false
			? { error: INVALID_CONNECTION_STRING }
			: { mode: SslMode.allow };
	}

	if (['true', 'yes', 'mandatory', '1'].includes(encrypt)) {
		return { mode: trust === true ? SslMode.require : SslMode.verifyFull };
	}

	return { error: INVALID_CONNECTION_STRING };
}

/**
 * Parse a SQL Server connection string (e.g. `sqlserver://user:pass@host:1433/db`)
 * into the internal fields sent to Engine. Public TLS options use SQL Server keywords.
 */
export function parseMssqlConnectionString(connectionString: string): MssqlConnectionStringParseResult {
	const result = parseConnectionString({
		connectionString,
		protocols: ['sqlserver:', 'mssql:'],
		sslKeys: [],
		sslAllowedModes: MSSQL_MODES,
		resolveSslMode: resolveMssqlSslMode,
	});

	return { ...result, options: result.options as Partial<MssqlOptions> };
}

const mssqlConnector = defineConnector<MssqlOptions>({
	id: 'mssql',
	provider: 'mssql',
	name: '$t:connectors.mssql.name',
	description: '$t:connectors.mssql.description',
	type: ConnectorType.Relational,
	availableSince: '2026-07-14',

	icon: 'simple-icons:microsoftsqlserver',
	iconBackground: '#CC2927',
	iconForeground: '#FFFFFF',

	keywords: ['mssql', 'sql server', 'sqlserver', 'microsoft', 'relational', 'sql'],

	options: ({ form, values }) => form([
		{
			name: 'connectionString',
			label: '$t:connectors.common.connection_string',
			block: BuiltinBlockIds.InputTextarea,
			description: '$t:connectors.common.connection_string_hint',
			fieldProps: {
				layout: 'stacked',
			},
			props: {
				class: 'font-mono font-medium',
				rows: 2,
				placeholder: 'sqlserver://user:password@host:1433/database',
			},
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
		},
		{
			name: 'host',
			label: '$t:common.host',
			description: '$t:connectors.common.host_description',
			block: BuiltinBlockIds.Input,
			required: true,
		},
		{
			name: 'port',
			label: '$t:common.port',
			description: '$t:connectors.common.port_description',
			block: BuiltinBlockIds.InputNumber,
			required: true,
			props: {
				formatOptions: {
					useGrouping: false,
				},
				min: 0,
				max: 65_535,
			},
			default: () => 1433,
		},
		{
			name: 'user',
			label: '$t:common.user',
			description: '$t:connectors.common.user_description',
			block: BuiltinBlockIds.Input,
			required: true,
			props: {
				autocomplete: 'off',
			},
		},
		{
			name: 'password',
			label: '$t:common.password',
			description: '$t:connectors.common.password_description',
			block: BuiltinBlockIds.InputPassword,
			required: true,
			props: {
				autocomplete: 'off',
			},
		},
		{
			name: 'dbname',
			label: '$t:connectors.common.database',
			description: '$t:connectors.common.database_description',
			block: BuiltinBlockIds.Input,
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
		},
		{
			name: 'ssl.mode',
			label: '$t:connectors.common.ssl_mode',
			description: '$t:connectors.common.ssl_mode_description',
			block: BuiltinBlockIds.InputSelect,
			props: {
				options: MSSQL_SSL_MODE_OPTIONS,
			},
			default: () => 'allow',
		},
		// Additional trust roots only apply to verify-full; allow and require reject them.
		{
			...caCertField(values),
			name: 'ssl.additional_ca_cert',
			label: '$t:connectors.common.additional_ca_cert',
			description: '$t:connectors.common.additional_ca_cert_description',
			hidden: values.ssl?.mode !== SslMode.verifyFull,
		},
	]),

	schema: mssqlSchema,

	onOptionsChange: createConnectionStringOptionsChangeHandler<MssqlOptions>({
		parse: parseMssqlConnectionString,
		invalidMessage: INVALID_CONNECTION_STRING,
		fields: ['host', 'port', 'user', 'password', 'dbname', 'ssl'],
		requiredFields: ['host', 'user', 'password'],
	}),

	// Transform form values to the mssql schema (strips connectionString).
	transformOptions: (opts) => ({
		host: opts.host,
		port: opts.port,
		user: opts.user,
		password: opts.password,
		dbname: opts.dbname,
		// Send the additional CA only with verify-full, because its hidden field keeps the value after a mode switch.
		ssl: opts.ssl.mode === SslMode.verifyFull ? opts.ssl : { mode: opts.ssl.mode },
	}),

	supportedPrimitiveTypes: [...(new Set(SUPPORTED_PRIMITIVE_TYPES).difference(new Set(PRIMITIVE_TYPES_UNSIGNED_INTEGER)))],
});

export default mssqlConnector;
